import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../../utils/apiService";
import MessageHeaderLayer from "./MessageHeaderLayer";
import CodeSamplesPanel from "./CodeSamplesPanel";
import ToggleBar from "../child/ToggleBar";
import "./ApiTestPage.css";

const SingleLayer = () => {
  const [templates, setTemplates] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [data, setData] = useState({
    client_id: null,
    content: { text: "" },
    fromTemplet: false,
    token: null,
    message: "",
    name: null,
    mobile: "",
    onlySave: false,
    saveAsTemplet: false,
    type: "text",
  });

  const fetchTemplates = async (type) => {
    try {
      const response = await apiCall({
        url: `templet/get-templet`,
        method: "POST",
        body: { type },
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage("");
      setSpinner(false);
    }, sec * 1000);
  };

  const cleanForm = () => {
    setSpinner(false);
    hideAlert(2);
  };

  const addNewTemplate = (newTemplate) => {
    setTemplates([newTemplate, ...templates]);
  };

  const validated = () => {
    if (!data.client_id) return { isValid: false, messageText: "Instance is required!" };
    if (!data.token) return { isValid: false, messageText: "Token is required!" };
    if (!data.mobile) return { isValid: false, messageText: "Phone is required!" };
    if (data.fromTemplet && !data.content?.text) {
      return { isValid: false, messageText: "Template is required!" };
    }
    if (!data.fromTemplet && !data.message) {
      return { isValid: false, messageText: "Message is required!" };
    }
    if (data.saveAsTemplet && !data.name) {
      return { isValid: false, messageText: "Template name is required!" };
    }
    return { isValid: true, messageText: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, messageText } = validated();
    if (!isValid) {
      setAlertMessage({ message: messageText, class: "danger" });
      hideAlert(2);
      return;
    }

    setSpinner(true);
    try {
      const response = await apiCall({
        url: "send-message/send",
        method: "POST",
        body: data,
      });

      setAlertMessage({
        message: response.msg,
        class: response.success ? "success" : "danger",
      });
      if (response.success) {
        if (response.template) addNewTemplate(response.template);
        cleanForm();
      } else {
        hideAlert(2);
      }
    } catch (error) {
      setAlertMessage({ message: error.message, class: "danger" });
      hideAlert(2);
    }
  };

  useEffect(() => {
    document.title = "Send Text";
    fetchTemplates("text");
  }, []);

  const sampleParams = useMemo(
    () => ({
      clientId: data.client_id,
      token: data.token,
      mobile: data.mobile,
      message: data.message || data.content?.text,
    }),
    [data.client_id, data.token, data.mobile, data.message, data.content?.text]
  );

  return (
    <div className="api-test-page">
      <div className="card">
        <div className="card-body">
          <div className="api-test-welcome">
            <div>
              <h5>Text message test</h5>
              <p>Send a WhatsApp text message and copy ready-to-use API snippets for your stack.</p>
            </div>
            <span className="api-test-badge">
              <Icon icon="mdi:api" width={14} />
              POST · Text
            </span>
          </div>

          <MessageHeaderLayer
            getHeaderData={(id, apiKey) =>
              setData((prev) => ({ ...prev, client_id: id || null, token: apiKey }))
            }
          />

          <div className="api-test-grid">
            <form className="api-test-card" onSubmit={handleSubmit}>
              <div className="api-test-card-head">
                <h6>Request test</h6>
                <span>Live send</span>
              </div>
              <div className="api-test-card-body">
                <div className="api-test-note">
                  <Icon icon="mdi:information-outline" width={18} />
                  <span>Use it wisely — not recommended for bulk numbers from this tester.</span>
                </div>

                {alertMessage && (
                  <div className={`api-test-alert ${alertMessage.class}`}>{alertMessage.message}</div>
                )}

                <label className="api-test-label" htmlFor="text-phone">
                  Phone
                </label>
                <input
                  id="text-phone"
                  type="text"
                  className="form-control"
                  placeholder="923037900571"
                  value={data.mobile || ""}
                  onChange={(e) => setData({ ...data, mobile: e.target.value })}
                />
                <span className="api-test-help">Mobile number with country code, without +</span>

                <ToggleBar
                  id="text-template-switch"
                  label="Using template?"
                  checked={data.fromTemplet}
                  onChange={(checked) =>
                    setData({ ...data, fromTemplet: checked, saveAsTemplet: false })
                  }
                />

                {!data.fromTemplet ? (
                  <>
                    <label className="api-test-label" htmlFor="text-message">
                      Message
                    </label>
                    <textarea
                      id="text-message"
                      rows={5}
                      className="form-control"
                      placeholder="Type message here..."
                      value={data.message || ""}
                      onChange={(e) =>
                        setData({
                          ...data,
                          message: e.target.value,
                          content: { ...data.content, text: e.target.value },
                        })
                      }
                    />

                    <ToggleBar
                      id="text-save-template"
                      label="Save as template?"
                      checked={data.saveAsTemplet}
                      onChange={(checked) =>
                        setData({ ...data, saveAsTemplet: checked })
                      }
                    />

                    {data.saveAsTemplet && (
                      <>
                        <label className="api-test-label" htmlFor="text-template-name">
                          Template name
                        </label>
                        <input
                          id="text-template-name"
                          type="text"
                          className="form-control"
                          value={data.name || ""}
                          onChange={(e) => setData({ ...data, name: e.target.value })}
                          placeholder="Template name"
                        />
                        <ToggleBar
                          id="text-only-save"
                          label="Save template only"
                          description="Do not send WhatsApp message — save template only."
                          checked={data.onlySave}
                          onChange={(checked) =>
                            setData({ ...data, onlySave: checked })
                          }
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <label className="api-test-label" htmlFor="text-template-select">
                      Template
                    </label>
                    <select
                      id="text-template-select"
                      className="form-select"
                      defaultValue=""
                      onChange={(e) =>
                        setData({
                          ...data,
                          message: e.target.value,
                          content: { ...data.content, text: e.target.value },
                        })
                      }
                    >
                      <option value="" disabled>
                        Select Template
                      </option>
                      {templates.map((template) => {
                        let text = "";
                        try {
                          text = JSON.parse(template.content)?.text || "";
                        } catch {
                          text = "";
                        }
                        return (
                          <option key={template.id} value={text}>
                            {template.name}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}

                <div className="api-test-actions">
                  <button type="submit" className="btn api-test-submit" disabled={spinner}>
                    {spinner ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </div>
            </form>

            <CodeSamplesPanel kind="text" params={sampleParams} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleLayer;
